import * as DropdownMenu from "@radix-ui/react-dropdown-menu"
import clsx from "clsx"
import { useRef, useState } from "react"

import useIsDesktop from "@hooks/useIsDesktop"
import useLayerStack from "@hooks/useLayerStack"

import { Divider } from "@atoms"

import { MenuItem, Popover } from "@molecules"
import { DefaultMenuItemProps } from "@molecules/MenuItem/MenuItem.types"

import { ComponentNavigation } from "@organisms"

import styles from "./Menu.module.scss"
import { MenuContentNode, MenuItemType, MenuLabelItem, MenuProps } from "./Menu.types"

export default function Menu(props: MenuProps) {
    const {
        title,
        supportText,
        headerLeft,
        trigger,
        items,
        className,
        isOpen,
        setIsOpen,
        popoverSide = "right",
        popoverAlign = "end",
    } = props

    const isDesktop = useIsDesktop()
    const closedByPointer = useRef(false)
    const [internalIsOpen, setInternalIsOpen] = useState(false)
    const isMenuOpen = isOpen ?? internalIsOpen
    const handleOpenChange = (open: boolean) => {
        setIsOpen?.(open)
        if (typeof isOpen !== "boolean") {
            setInternalIsOpen(open)
        }
    }
    const { floatingZIndex } = useLayerStack(isDesktop ? isMenuOpen : false)

    const renderLabelItem = (labelItem: MenuLabelItem, key: number) => {
        return (
            <div key={key} className={clsx(styles.menuItemLabel, labelItem.className)}>
                <span className={styles.menuItemLabelText}>{labelItem.text}</span>
                {labelItem.supportText && (
                    <span className={styles.menuItemLabelSupportText}>{labelItem.supportText}</span>
                )}
            </div>
        )
    }

    const renderDesktopNodes = (nodes: MenuContentNode[]) => {
        return nodes.map((node, nodeIndex) => {
            if (node.type === "divider") {
                return (
                    <div key={nodeIndex} className={styles.divider}>
                        <Divider />
                    </div>
                )
            } else {
                return (
                    <div key={nodeIndex} className={styles.menuGroup}>
                        {node.items.map((menuItems: MenuItemType, itemIndex: number) => {
                            if ("type" in menuItems && menuItems.type === "labelItem") {
                                if (menuItems.hideOnMobile && !isDesktop) {
                                    return null
                                }
                                return renderLabelItem(menuItems, itemIndex)
                            }
                            const { submenu, title, className, ...rest } = menuItems as Exclude<
                                MenuItemType,
                                { type: "labelItem" }
                            >
                            if (submenu) {
                                return (
                                    <DropdownMenu.Sub key={itemIndex}>
                                        <DropdownMenu.SubTrigger asChild={true}>
                                            <MenuItem
                                                {...(rest as DefaultMenuItemProps)}
                                                title={title}
                                                showSubMenu={true}
                                            />
                                        </DropdownMenu.SubTrigger>
                                        <DropdownMenu.Portal>
                                            <DropdownMenu.SubContent
                                                className={clsx(styles.menuContent, submenu.className)}
                                                sideOffset={4}
                                                collisionPadding={4}
                                                loop={true}
                                                avoidCollisions={true}
                                                style={{ zIndex: floatingZIndex }}
                                            >
                                                {renderDesktopNodes(submenu.items)}
                                            </DropdownMenu.SubContent>
                                        </DropdownMenu.Portal>
                                    </DropdownMenu.Sub>
                                )
                            } else if (rest.variant === "toggle") {
                                // CheckboxItem, not Item, is the intentional primitive for toggle rows: it
                                // gives the row the menu-checkbox role with `aria-checked`, and the prevented
                                // select keeps the menu open on a flip. The flip itself stays with the row's
                                // own click, so no `onCheckedChange` here.
                                return (
                                    <DropdownMenu.CheckboxItem
                                        asChild={true}
                                        key={itemIndex}
                                        checked={rest.toggle.isChecked}
                                        onSelect={(event) => event.preventDefault()}
                                    >
                                        <MenuItem title={title} className={className} {...rest} />
                                    </DropdownMenu.CheckboxItem>
                                )
                            } else {
                                return (
                                    <DropdownMenu.Item asChild={true} key={itemIndex}>
                                        <MenuItem title={title} className={className} {...rest} />
                                    </DropdownMenu.Item>
                                )
                            }
                        })}
                    </div>
                )
            }
        })
    }

    const renderMobilePrimary = (nodes: MenuContentNode[]) => {
        const renderNodes = (levelNodes: MenuContentNode[], parentId: string) => {
            return levelNodes.map((node, groupIndex) => {
                if (node.type === "divider") {
                    return (
                        <div key={groupIndex} className={styles.divider}>
                            <Divider />
                        </div>
                    )
                } else {
                    return (
                        <div key={groupIndex} className={styles.menuGroup}>
                            {node.items.map((menuItems: MenuItemType, itemIndex: number) => {
                                if ("type" in menuItems && menuItems.type === "labelItem") {
                                    if (menuItems.hideOnMobile) {
                                        return null
                                    }
                                    return renderLabelItem(menuItems, itemIndex)
                                }
                                const { submenu, ...rest } = menuItems as Exclude<MenuItemType, { type: "labelItem" }>
                                const childId = `${parentId}-${groupIndex}-${itemIndex}`
                                if (submenu) {
                                    return (
                                        <ComponentNavigation.Navigate key={childId} to={childId} asChild={true}>
                                            <MenuItem {...(rest as DefaultMenuItemProps)} showSubMenu={true} />
                                        </ComponentNavigation.Navigate>
                                    )
                                } else {
                                    return <MenuItem key={childId} {...rest} />
                                }
                            })}
                        </div>
                    )
                }
            })
        }

        return (
            <Popover.View componentId="primary">
                <Popover.Section animationType="fade">
                    <Popover.Header.Root>
                        {title && <Popover.Header.Title title={title} caption={supportText} avatar={headerLeft} />}
                    </Popover.Header.Root>
                </Popover.Section>
                <Popover.Section animationType="slide">
                    <Popover.Body>{renderNodes(nodes, "primary")}</Popover.Body>
                </Popover.Section>
            </Popover.View>
        )
    }

    const buildMobileSubViews = (nodes: MenuContentNode[], parentId: string): React.ReactNode[] => {
        const views: React.ReactNode[] = []

        const renderNodes = (levelNodes: MenuContentNode[], currentId: string) => {
            return levelNodes.map((node, groupIndex) => {
                if (node.type === "divider") {
                    return (
                        <div key={groupIndex} className={styles.divider}>
                            <Divider />
                        </div>
                    )
                } else {
                    return (
                        <div key={groupIndex} className={styles.menuGroup}>
                            {node.items.map((menuItems: MenuItemType, itemIndex: number) => {
                                if ("type" in menuItems && menuItems.type === "labelItem") {
                                    if (menuItems.hideOnMobile) {
                                        return null
                                    }
                                    return renderLabelItem(menuItems, itemIndex)
                                }
                                const { submenu, ...rest } = menuItems as Exclude<MenuItemType, { type: "labelItem" }>
                                const childId = `${currentId}-${groupIndex}-${itemIndex}`
                                if (submenu) {
                                    return (
                                        <ComponentNavigation.Navigate key={childId} to={childId} asChild={true}>
                                            <MenuItem {...(rest as DefaultMenuItemProps)} showSubMenu={true} />
                                        </ComponentNavigation.Navigate>
                                    )
                                } else {
                                    return <MenuItem key={childId} {...rest} />
                                }
                            })}
                        </div>
                    )
                }
            })
        }

        nodes.forEach((node, groupIndex) => {
            if (node.type === "group") {
                node.items.forEach((menuItems, itemIndex) => {
                    if ("type" in menuItems && menuItems.type === "labelItem") {
                        return
                    }
                    const { submenu } = menuItems as Exclude<MenuItemType, { type: "labelItem" }>
                    if (submenu) {
                        const currentId = `${parentId}-${groupIndex}-${itemIndex}`
                        const header = submenu.header
                        views.push(
                            <Popover.View componentId={currentId} key={currentId} className={submenu.className}>
                                <Popover.Section animationType="fade">
                                    <Popover.Header.Root>
                                        {header?.title && (
                                            <>
                                                <Popover.Header.BackButton
                                                    backDestination={parentId}
                                                    aria-label="Back"
                                                />
                                                <Popover.Header.Title title={header.title} />
                                                {header.showCloseButton && <Popover.Header.CloseButton />}
                                            </>
                                        )}
                                    </Popover.Header.Root>
                                </Popover.Section>
                                <Popover.Section animationType="slide">
                                    <Popover.Body>{renderNodes(submenu.items, currentId)}</Popover.Body>
                                </Popover.Section>
                            </Popover.View>,
                        )

                        // Recurse into deeper submenus
                        views.push(...buildMobileSubViews(submenu.items, currentId))
                    }
                })
            }
        })

        return views
    }

    if (isDesktop) {
        return (
            <DropdownMenu.Root open={isMenuOpen} onOpenChange={handleOpenChange}>
                <DropdownMenu.Trigger asChild={true}>{trigger}</DropdownMenu.Trigger>
                <DropdownMenu.Portal>
                    <DropdownMenu.Content
                        className={clsx(styles.menuContent, className)}
                        sideOffset={4}
                        collisionPadding={4}
                        avoidCollisions={true}
                        loop={true}
                        side={popoverSide}
                        align={popoverAlign}
                        onPointerDownOutside={() => {
                            closedByPointer.current = true
                        }}
                        onCloseAutoFocus={(event) => {
                            if (closedByPointer.current) {
                                event.preventDefault()
                                closedByPointer.current = false
                            }
                        }}
                        style={{ zIndex: floatingZIndex }}
                    >
                        {renderDesktopNodes(items)}
                    </DropdownMenu.Content>
                </DropdownMenu.Portal>
            </DropdownMenu.Root>
        )
    } else {
        return (
            <Popover.Root isOpen={isMenuOpen} onOpenChange={handleOpenChange}>
                <Popover.Trigger>{trigger}</Popover.Trigger>
                <Popover.Viewport side="right" align="end">
                    {renderMobilePrimary(items)}
                    {buildMobileSubViews(items, "primary")}
                </Popover.Viewport>
            </Popover.Root>
        )
    }
}
